import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientRenderRuntimeBridgeService } from '../../../core/services/client-render-runtime-bridge.service';
import { APP_POLLING_INTERVALS } from '../../../core/constants/polling-intervals.constants';
import { BackgroundRenderClientService } from '../../../core/render-runtime/worker/background-render-client.service';
import { RenderJobResult } from '../../../core/render-runtime/worker/render-job.model';
import {
  ReviewBrowserRenderResult,
  ReviewClientRenderRuntime
} from '../../Review/models/review-browser-render.model';
import { ReviewCaptionSegment } from '../../Review/models/review-video.model';
import { ReviewService } from '../../Review/services/review.service';
import {
  buildProcessingRenderJob,
  ProcessingRenderJobArgs
} from '../../Review/services/render-job/processing-render-job.adapter';
import {
  ProcessingClientRenderPayload,
  ProcessingClientRenderShort,
  ProcessingJobDetailedStatus,
  ProcessingJobApiService,
  ProcessingRenderComposition,
  ProcessingReviewTargetReservation
} from './processing-job-api.service';
import { ProcessingRuntimeStatusReporter } from './processing-runtime-status-reporter.service';
import {
  buildSmartVideoRuntimeReviewCheckpoint,
  parseSmartVideoRuntimeCheckpointResources,
  SmartVideoRuntimeCheckpointState,
  SmartVideoRuntimeReviewCheckpoint
} from '../../Rendering/non-editor/smart-video/smart-video-runtime-checkpoints';

type StatusCallback = (message: string) => void;

@Injectable({ providedIn: 'root' })
export class ProcessingRuntimeRenderService {
  private readonly api = inject(ProcessingJobApiService);
  private readonly reviewService = inject(ReviewService);
  private readonly backgroundRender = inject(BackgroundRenderClientService);
  private readonly renderRuntime = inject(ClientRenderRuntimeBridgeService);
  private readonly statusReporter = inject(ProcessingRuntimeStatusReporter);
  private lastProgressUpdateAt = 0;

  async render(payload: {
    file: File;
    clientRender: ProcessingClientRenderPayload;
    options: any;
    fallbackJobId: string | null;
    checkpointResourcesJson?: string | null;
    onStatus?: StatusCallback;
  }): Promise<void> {
    if (!this.backgroundRender.supported()) {
      throw new Error('This browser cannot render video (WebCodecs/Worker unavailable).');
    }

    await this.renderRuntime.loadRuntimeLimits();

    const jobId = payload.clientRender.jobId || payload.fallbackJobId;
    const sourceUrl = URL.createObjectURL(payload.file);
    const sourceUrlCandidates = this.desktopSourceCandidates(payload.file);
    const segments = this.normalizeSegments(payload.clientRender.segments);
    const duration = this.positiveNumber(payload.clientRender.durationSeconds)
      ?? this.segmentEnd(segments)
      ?? 1;
    const runtimeDecision = this.renderRuntime.resolve({
      surface: 'processing-video',
      durationSeconds: duration,
      preferDesktopPlugin: true,
      // Encode runs entirely in the background worker; prefer WebCodecs so a
      // worker-capable browser is never routed onto the legacy MediaRecorder path.
      preferWebCodecs: true,
      requiresVideoEncoding: true
    });

    if (!runtimeDecision.allowed) {
      throw new Error(runtimeDecision.message || 'This device cannot render the processing result locally.');
    }

    const runtime = (runtimeDecision.runtime || payload.clientRender.runtime || 'browser') as ReviewClientRenderRuntime;
    const checkpointState = await this.loadRuntimeReviewCheckpoints(jobId, payload.checkpointResourcesJson);
    const title = payload.clientRender.title?.trim() || this.titleFromFileName(payload.file.name);
    const transcript = (payload.clientRender.transcript || segments.map(segment => segment.text).join(' ')).trim();
    const description = this.descriptionFromPayload(payload.clientRender.description, transcript, title);
    const hook = payload.clientRender.metadata?.hook || payload.clientRender.mainComposition?.hook || this.hookFromText(transcript || description || title);
    const script = payload.clientRender.metadata?.script || payload.clientRender.mainComposition?.script || transcript;
    const createMainVideo = payload.options.createMainVideo !== false;
    const createShorts = payload.options.createShorts === true;
    const thumbnailCount = this.thumbnailCount(payload.options?.thumbnailCount);

    if (!createMainVideo && !createShorts) {
      throw new Error('Select at least one output: main video or shorts.');
    }

    try {
      // Render each output independently so one failure does not discard the
      // outputs that already saved. The job is marked completed when at least one
      // output is saved (partial success); it only fails when nothing saved.
      let savedCount = 0;
      let attemptedCount = 0;
      const savedReviewIds: string[] = [];
      const skippedReviewIds: string[] = [];
      const failures: string[] = [];

      if (createMainVideo) {
        attemptedCount++;
        if (checkpointState.hasMain && checkpointState.main) {
          savedCount++;
          savedReviewIds.push(checkpointState.main.reviewId);
          skippedReviewIds.push(checkpointState.main.reviewId);
          payload.onStatus?.('Main video already saved to Review. Continuing with missing outputs...');
        } else {
          try {
            await this.ensureCanContinue(jobId);
            const checkpoint = await this.renderAndSaveRuntimeVideo({
            jobId,
            sourceBlob: payload.file,
            sourceUrl,
            sourceUrlCandidates,
            runtime,
            title,
            description,
            hook,
            script,
            transcript,
            renderSegments: segments,
            saveSegments: segments,
            sourceDurationSeconds: duration,
            startTime: 0,
            endTime: duration,
            isShort: false,
            progressBase: 72,
            progressRange: createShorts ? 10 : 20,
            statusStep: 'RenderMainVideo',
            statusLabel: 'Processing main video',
            outputIndex: null,
            composition: payload.clientRender.mainComposition ?? null,
            burnSubtitles: this.shouldBurnSubtitles(payload.options),
            thumbnailCount,
            onStatus: payload.onStatus
          });
            savedCount++;
            savedReviewIds.push(checkpoint.reviewId);
          } catch (err) {
            failures.push(`main video (${this.errorText(err)})`);
            console.warn('Processing main video failed; continuing with remaining outputs.', err);
          }
        }
      }

      if (createShorts) {
        const requestedShorts = Math.max(1, Math.trunc(Number(payload.options.maxShorts ?? payload.clientRender.shorts?.length ?? 1)));
        const shorts = this.normalizeShorts(payload.clientRender.shorts, duration, segments, title, description).slice(0, requestedShorts);

        for (let index = 0; index < shorts.length; index++) {
          const item = shorts[index];
          const progressBase = 84 + Math.round(index * (10 / Math.max(1, shorts.length)));
          const composition = (item.composition ?? null) as (ProcessingRenderComposition & Record<string, unknown>) | null;
          const startTime = Math.max(0, this.finiteNumber(composition?.startTime ?? composition?.['StartTime']) ?? item.startTime ?? 0);
          const durationSeconds = this.positiveNumber(composition?.durationSeconds ?? composition?.['DurationSeconds'])
            ?? this.positiveNumber(item.durationSeconds)
            ?? 60;
          const requestedEnd = this.finiteNumber(composition?.endTime ?? composition?.['EndTime'])
            ?? item.endTime
            ?? startTime + durationSeconds;
          const endTime = Math.min(duration || requestedEnd, Math.max(startTime + 0.1, requestedEnd > startTime ? requestedEnd : startTime + durationSeconds));
          const compositionSegments = this.normalizeSegments(composition?.captions ?? composition?.['Captions']);
          const rawSourceSegments = compositionSegments.length
            ? compositionSegments
            : item.segments?.length
              ? item.segments
              : segments;
          const sourceSegments = this.alignSegmentsToSourceRange(rawSourceSegments, startTime, endTime);
          const renderSegments = this.segmentsInRange(sourceSegments, startTime, endTime, false);
          const saveSegments = this.segmentsInRange(sourceSegments, startTime, endTime, true);
          const segmentText = saveSegments.map(segment => segment.text).join(' ').trim();
          const shortScript = (
            this.optionalString(composition?.script ?? composition?.['Script'])
            || item.script
            || segmentText
            || transcript
            || title
          ).trim();
          const shortHook = (
            this.optionalString(composition?.hook ?? composition?.['Hook'])
            || item.hook
            || this.hookFromText(shortScript || item.title || title)
          ).trim();
          const shortDescription = this.descriptionFromPayload(item.description, segmentText || shortScript, item.title || title);

          attemptedCount++;
          if (checkpointState.hasShort(index)) {
            const checkpoint = checkpointState.shorts.get(index);
            savedCount++;
            if (checkpoint?.reviewId) {
              savedReviewIds.push(checkpoint.reviewId);
              skippedReviewIds.push(checkpoint.reviewId);
            }
            payload.onStatus?.(`Short ${index + 1} already saved to Review. Continuing with missing outputs...`);
            continue;
          }

          try {
            await this.ensureCanContinue(jobId);
            const checkpoint = await this.renderAndSaveRuntimeVideo({
              jobId,
              sourceBlob: payload.file,
              sourceUrl,
              sourceUrlCandidates,
              runtime,
              title: item.title || `${title} Short ${index + 1}`,
              description: shortDescription,
              hook: shortHook,
              script: shortScript,
              transcript: shortScript,
              renderSegments,
              saveSegments,
              sourceDurationSeconds: duration,
              startTime,
              endTime,
              isShort: true,
              progressBase,
              progressRange: 10 / Math.max(1, shorts.length),
              statusStep: `RenderShort${index + 1}`,
              statusLabel: `Processing short ${index + 1} of ${shorts.length}`,
              outputIndex: index,
              composition,
              burnSubtitles: this.shouldBurnSubtitles(payload.options),
              thumbnailCount,
              onStatus: payload.onStatus
            });
            savedCount++;
            savedReviewIds.push(checkpoint.reviewId);
          } catch (err) {
            failures.push(`short ${index + 1} (${this.errorText(err)})`);
            console.warn(`Processing short ${index + 1} failed; continuing with remaining outputs.`, err);
          }
        }
      }

      if (savedCount === 0) {
        throw new Error(failures.length
          ? `Processing failed: ${failures.join('; ')}`
          : 'Processing produced no outputs.');
      }

      if (failures.length) {
        // Partial success: keep the saved outputs and mark the job completed so they
        // are not hidden behind a failed status. The distinct step records the partial.
        payload.onStatus?.(`Saved ${savedCount} of ${attemptedCount} output${attemptedCount === 1 ? '' : 's'}; ${failures.length} failed.`);
        console.warn(`Processing completed partially: ${failures.join('; ')}`);
        await this.statusReporter.update(jobId, 'completed', 100, 'SavedToReviewPartial', null, {
          reviewIds: savedReviewIds,
          skippedReviewIds,
          failures,
          savedAtUtc: new Date().toISOString()
        });
      } else {
        await this.statusReporter.update(jobId, 'completed', 100, 'SavedToReview', null, {
          reviewIds: savedReviewIds,
          skippedReviewIds,
          savedAtUtc: new Date().toISOString()
        });
      }
    } finally {
      URL.revokeObjectURL(sourceUrl);
    }
  }

  private errorText(err: unknown): string {
    return err instanceof Error ? err.message : 'unknown error';
  }

  private shouldBurnSubtitles(options: unknown): boolean {
    if (!options || typeof options !== 'object') return false;
    const raw = options as Record<string, unknown>;
    return raw['burnSubtitles'] === true || raw['BurnSubtitles'] === true;
  }

  private desktopSourceCandidates(file: File): string[] {
    const path = this.desktopFilePath(file);
    return path ? [path] : [];
  }

  private desktopFilePath(file: File): string | null {
    const legacyPath = (file as File & { path?: string }).path;
    if (legacyPath) return legacyPath;

    try {
      return window.heyjivuDesktop?.fileSystem?.getPathForFile?.(file) || null;
    } catch {
      return null;
    }
  }

  private async loadRuntimeReviewCheckpoints(
    jobId: string | null | undefined,
    checkpointResourcesJson?: string | null
  ): Promise<SmartVideoRuntimeCheckpointState> {
    if (checkpointResourcesJson?.trim()) {
      return parseSmartVideoRuntimeCheckpointResources(checkpointResourcesJson);
    }

    if (!jobId) {
      return parseSmartVideoRuntimeCheckpointResources(null);
    }

    try {
      const status = await firstValueFrom(this.api.getProcessingJobStatus(jobId));
      return parseSmartVideoRuntimeCheckpointResources(status.checkpointResourcesJson);
    } catch {
      return parseSmartVideoRuntimeCheckpointResources(null);
    }
  }

  private async ensureCanContinue(jobId: string | null | undefined): Promise<ProcessingJobDetailedStatus | null> {
    if (!jobId) return null;

    try {
      const status = await firstValueFrom(this.api.getProcessingJobStatus(jobId));
      const normalized = String(status.status || '').trim().toLowerCase();
      if (normalized === 'paused') {
        throw new Error(status.errorMessage || 'Processing job is paused.');
      }
      if (normalized === 'cancelled' || normalized === 'canceled') {
        throw new Error(status.errorMessage || 'Processing job is cancelled.');
      }
      return status;
    } catch (error) {
      if (error instanceof Error && /processing job is (paused|cancelled|canceled)/i.test(error.message)) {
        throw error;
      }
      return null;
    }
  }

  private async renderAndSaveRuntimeVideo(args: {
    jobId: string | null | undefined;
    /** In-memory source video. Preferred carrier for the worker (transferable, no URL race). */
    sourceBlob: Blob;
    sourceUrl: string;
    sourceUrlCandidates?: string[];
    runtime: ReviewClientRenderRuntime;
    title: string;
    description: string;
    hook: string;
    script: string;
    transcript: string;
    renderSegments: ReviewCaptionSegment[];
    saveSegments: ReviewCaptionSegment[];
    sourceDurationSeconds: number;
    startTime: number;
    endTime: number;
    isShort: boolean;
    progressBase: number;
    progressRange: number;
    statusStep: string;
    statusLabel: string;
    outputIndex: number | null;
    composition: ProcessingRenderComposition | null;
    burnSubtitles: boolean;
    thumbnailCount: number;
    onStatus?: StatusCallback;
  }): Promise<SmartVideoRuntimeReviewCheckpoint> {
    await this.statusReporter.update(args.jobId, 'processing', Math.round(args.progressBase), args.statusStep);
    const renderDurationSeconds = Math.max(0.1, args.endTime - args.startTime);
    const runtimeDecision = this.renderRuntime.resolve({
      surface: 'processing-video',
      durationSeconds: renderDurationSeconds,
      preferDesktopPlugin: true,
      // Encode runs entirely in the background worker; prefer WebCodecs so a
      // worker-capable browser is never routed onto the legacy MediaRecorder path.
      preferWebCodecs: true,
      requiresVideoEncoding: true
    });

    if (!runtimeDecision.allowed) {
      throw new Error(runtimeDecision.message || 'This device cannot render the processing result locally.');
    }

    const rendered = await this.renderWithWorker(args, renderDurationSeconds);

    args.onStatus?.(`${args.statusLabel} — preparing…`);
    await this.statusReporter.update(args.jobId, 'processing', Math.round(args.progressBase + args.progressRange), args.isShort ? 'ReserveReviewShortTarget' : 'ReserveReviewMainTarget');
    if (!args.jobId) {
      throw new Error('Processing job id is required before reserving Review targets.');
    }

    const reservation = await firstValueFrom(this.api.reserveReviewTarget({
      jobId: args.jobId,
      title: args.title,
      description: args.description || null,
      isShort: args.isShort,
      videoContentType: rendered.contentType || rendered.mimeType || rendered.file?.type || 'video/mp4',
      thumbnailContentType: rendered.thumbnailContentType || rendered.thumbnailFile?.type || 'image/jpeg',
      thumbnailCount: args.thumbnailCount,
      durationSeconds: rendered.durationSeconds,
      metadata: {
        hook: args.hook,
        script: args.script,
        transcript: args.transcript,
        runtime: rendered.clientRuntime || args.runtime,
        segmentCount: args.saveSegments.length
      }
    }));

    args.onStatus?.(`${args.statusLabel} — uploading…`);
    await this.uploadRenderedAssets(rendered, reservation, percent => {
      args.onStatus?.(`${args.statusLabel} — uploading ${percent}%`);
    });

    args.onStatus?.(`${args.statusLabel} — saving…`);
    const saved = await firstValueFrom(this.reviewService.saveClientRenderedVideo(reservation.reviewItemId, {
      title: args.title,
      description: args.description || null,
      hook: args.hook || null,
      script: args.script || null,
      videoFileId: reservation.videoUpload.filePath,
      videoFileName: this.fileNameFromPath(reservation.videoUpload.filePath),
      videoContentType: reservation.videoUpload.contentType || rendered.contentType || rendered.mimeType || 'video/mp4',
      thumbnailFileId: reservation.thumbnailUpload?.filePath ?? null,
      thumbnailFileName: reservation.thumbnailUpload ? this.fileNameFromPath(reservation.thumbnailUpload.filePath) : null,
      thumbnailContentType: reservation.thumbnailUpload?.contentType ?? null,
      durationSeconds: rendered.durationSeconds,
      clientRuntime: rendered.clientRuntime || args.runtime,
      captionTranscript: args.script || args.transcript,
      captionSegments: args.saveSegments,
      processingJobId: args.jobId || null,
      processingCheckpointKind: args.isShort ? 'short' : 'main',
      processingCheckpointIndex: args.isShort ? args.outputIndex ?? 0 : null
    }));

    const checkpoint = buildSmartVideoRuntimeReviewCheckpoint({
      kind: args.isShort ? 'short' : 'main',
      index: args.isShort ? args.outputIndex ?? 0 : null,
      reviewId: saved.id,
      folderId: saved.folderId,
      videoUrl: saved.directUrl ?? reservation.videoUpload.filePath,
      title: saved.title || args.title
    });

    await this.statusReporter.update(
      args.jobId,
      'processing',
      Math.round(args.progressBase + args.progressRange),
      checkpoint.step,
      null,
      checkpoint.stepOutputJson);

    return checkpoint.checkpoint;
  }

  /**
   * Render one output through the background render worker (the sole render path)
   * and adapt its RenderJobResult back into the ReviewBrowserRenderResult shape the
   * reserve/upload/save steps already consume.
   *
   * The Processing/Jivu case is the plain trim case: a single source video cut to
   * [startTime,endTime] with optional burned captions — no media-timeline clips,
   * soundtracks, overlays or feature layers. The adapter rebases the output timeline
   * to 0, so captions are passed on the rebased (output) timeline via `saveSegments`
   * (for the main video startTime=0, so it equals `renderSegments`; for shorts it is
   * the 0-based copy that keeps captions aligned with the rebased video).
   */
  private async renderWithWorker(
    args: {
      jobId: string | null | undefined;
      sourceBlob: Blob;
      sourceDurationSeconds: number;
      startTime: number;
      endTime: number;
      isShort: boolean;
      burnSubtitles: boolean;
      transcript: string;
      saveSegments: ReviewCaptionSegment[];
      thumbnailCount: number;
      progressBase: number;
      progressRange: number;
      statusStep: string;
      statusLabel: string;
      onStatus?: StatusCallback;
    },
    renderDurationSeconds: number
  ): Promise<ReviewBrowserRenderResult> {
    const jobArgs: ProcessingRenderJobArgs = {
      id: args.jobId ?? null,
      // In-memory source video (a File is a Blob); blob is the worker's preferred,
      // transferable carrier — no object-URL lifecycle race.
      sourceVideoBlob: args.sourceBlob,
      sourceVideoUrl: null,
      sourceDurationSeconds: this.positiveNumber(args.sourceDurationSeconds) ?? renderDurationSeconds,
      isShort: args.isShort,
      // Cut window of the source for this output (rebased to 0 on the output timeline).
      startTime: args.startTime,
      endTime: args.endTime,
      captions: {
        enabled: args.burnSubtitles,
        text: args.transcript,
        segments: args.saveSegments
      },
      thumbnailCount: args.thumbnailCount
    };

    const job = buildProcessingRenderJob(jobArgs);

    const handle = this.backgroundRender.start(job, p => {
      const mapped = args.progressBase + Math.max(0, Math.min(1, p.progress || 0)) * args.progressRange;
      args.onStatus?.(`${args.statusLabel} — ${Math.round(Math.max(0, Math.min(1, p.progress || 0)) * 100)}%`);
      const now = Date.now();
      if (now - this.lastProgressUpdateAt > APP_POLLING_INTERVALS.runtimeProgressStatusUpdateMs || (p.progress || 0) >= 0.99) {
        this.lastProgressUpdateAt = now;
        void this.statusReporter.update(args.jobId, 'processing', Math.round(mapped), args.statusStep);
      }
    });

    let out: RenderJobResult;
    try {
      out = await handle.result;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Background render failed.');
    }

    // Adapt RenderJobResult → ReviewBrowserRenderResult (the shape reserve/upload/save expect).
    const stamp = Date.now();
    const mimeType = out.mimeType || 'video/mp4';
    const file = new File([out.blob], `processing-render-${stamp}.mp4`, { type: mimeType });
    const thumbnailFiles = (out.thumbnails || []).map((blob, index) =>
      new File([blob], `processing-render-thumb-${stamp}-${index + 1}.jpg`, { type: blob.type || 'image/jpeg' })
    );

    return {
      file,
      contentType: mimeType,
      thumbnailFile: thumbnailFiles[0] ?? null,
      thumbnailFiles: thumbnailFiles.length ? thumbnailFiles : null,
      thumbnailContentType: thumbnailFiles[0]?.type ?? 'image/jpeg',
      durationSeconds: out.durationSeconds,
      width: out.width,
      height: out.height,
      mimeType,
      clientRuntime: 'browser'
    };
  }

  private async uploadRenderedAssets(
    rendered: ReviewBrowserRenderResult,
    reservation: ProcessingReviewTargetReservation,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    if (rendered.file) {
      await this.api.uploadBlobToTarget(
        rendered.file,
        reservation.videoUpload,
        reservation.videoUpload.contentType || rendered.contentType || rendered.mimeType || rendered.file.type,
        onProgress
      );
    } else if (rendered.clientRuntime === 'mobile') {
      await this.api.uploadMobileNativeFileToTarget(
        rendered.filePath || '',
        reservation.videoUpload,
        reservation.videoUpload.contentType || rendered.contentType || rendered.mimeType
      );
    } else {
      await this.api.uploadDesktopFileToTarget(
        rendered.filePath || '',
        reservation.videoUpload,
        reservation.videoUpload.contentType || rendered.contentType || rendered.mimeType
      );
    }

    const thumbnailTargets = reservation.thumbnailUploads?.length
      ? reservation.thumbnailUploads
      : reservation.thumbnailUpload
        ? [reservation.thumbnailUpload]
        : [];
    if (!thumbnailTargets.length) return;

    const thumbnailFiles = rendered.thumbnailFiles?.length
      ? rendered.thumbnailFiles
      : rendered.thumbnailFile
        ? [rendered.thumbnailFile]
        : [];
    for (let index = 0; index < Math.min(thumbnailTargets.length, thumbnailFiles.length); index++) {
      const file = thumbnailFiles[index];
      const target = thumbnailTargets[index];
      await this.api.uploadBlobToTarget(
        file,
        target,
        target.contentType || rendered.thumbnailContentType || file.type || 'image/jpeg'
      );
    }

    if (!thumbnailFiles.length && rendered.thumbnailFilePath && reservation.thumbnailUpload) {
      if (rendered.clientRuntime === 'mobile') {
        await this.api.uploadMobileNativeFileToTarget(
          rendered.thumbnailFilePath,
          reservation.thumbnailUpload,
          reservation.thumbnailUpload.contentType || rendered.thumbnailContentType || 'image/jpeg'
        );
      } else {
        await this.api.uploadDesktopFileToTarget(
          rendered.thumbnailFilePath,
          reservation.thumbnailUpload,
          reservation.thumbnailUpload.contentType || rendered.thumbnailContentType || 'image/jpeg'
        );
      }
    }
  }

  private normalizeSegments(value: unknown): ReviewCaptionSegment[] {
    if (!Array.isArray(value)) return [];

    return value
      .map(item => {
        const raw = item as Record<string, unknown>;
        const start = Number(raw['start'] ?? raw['Start']);
        const end = Number(raw['end'] ?? raw['End']);
        const text = String(raw['text'] ?? raw['Text'] ?? '').replace(/\s+/g, ' ').trim();
        return { start, end, text };
      })
      .filter(segment => Number.isFinite(segment.start) && Number.isFinite(segment.end) && segment.end > segment.start && !!segment.text);
  }

  private normalizeShorts(
    value: unknown,
    duration: number,
    fallbackSegments: ReviewCaptionSegment[],
    fallbackTitle: string,
    fallbackDescription: string
  ): ProcessingClientRenderShort[] {
    const shorts = Array.isArray(value)
      ? value.map((item, index) => {
          const raw = item as Record<string, unknown>;
          const composition = (raw['composition'] ?? raw['Composition'] ?? null) as Record<string, unknown> | null;
          const rawSegments = this.normalizeSegments(raw['segments'] ?? raw['Segments'] ?? composition?.['captions'] ?? composition?.['Captions']);
          const segmentText = rawSegments.map(segment => segment.text).join(' ').trim();
          const startTime = Number(composition?.['startTime'] ?? composition?.['StartTime'] ?? raw['startTime'] ?? raw['StartTime']);
          const endTime = Number(composition?.['endTime'] ?? composition?.['EndTime'] ?? raw['endTime'] ?? raw['EndTime']);
          const rawDuration = Number(composition?.['durationSeconds'] ?? composition?.['DurationSeconds'] ?? raw['durationSeconds'] ?? raw['DurationSeconds'] ?? 60);
          const normalizedStart = Number.isFinite(startTime) ? Math.max(0, startTime) : 0;
          const normalizedEnd = Number.isFinite(endTime) && endTime > normalizedStart
            ? Math.min(duration || endTime, endTime)
            : Math.min(duration || 60, normalizedStart + rawDuration);
          return {
            title: this.optionalString(raw['title'] ?? raw['Title']) || `${fallbackTitle || 'Processing'} Short ${index + 1}`,
            description: this.descriptionFromPayload(
              this.optionalString(raw['description'] ?? raw['Description']),
              segmentText,
              fallbackDescription
            ),
            startTime: normalizedStart,
            endTime: Math.max(normalizedStart + 0.1, normalizedEnd),
            durationSeconds: Math.max(0.1, normalizedEnd - normalizedStart),
            segments: rawSegments,
            hook: this.optionalString(raw['hook'] ?? raw['Hook'] ?? composition?.['hook'] ?? composition?.['Hook']),
            script: this.optionalString(raw['script'] ?? raw['Script'] ?? composition?.['script'] ?? composition?.['Script']),
            composition: composition as ProcessingRenderComposition | null
          } as ProcessingClientRenderShort;
        }).filter(item => item.endTime > item.startTime)
      : [];

    if (shorts.length || !fallbackSegments.length) return shorts;

    const endTime = Math.min(duration || this.segmentEnd(fallbackSegments) || 60, 60);
    const script = fallbackSegments.map(segment => segment.text).join(' ').trim() || fallbackDescription;
    return [{
      title: `${fallbackTitle || 'Processing'} Short 1`,
      description: fallbackDescription,
      startTime: 0,
      endTime,
      durationSeconds: endTime,
      segments: this.segmentsInRange(fallbackSegments, 0, endTime, false),
      hook: this.hookFromText(script),
      script
    }];
  }

  private alignSegmentsToSourceRange(segments: ReviewCaptionSegment[], startTime: number, endTime: number): ReviewCaptionSegment[] {
    const normalized = this.normalizeSegments(segments);
    if (!normalized.length) return [];

    if (normalized.some(segment => segment.end > startTime && segment.start < endTime)) {
      return normalized;
    }

    const renderDuration = Math.max(0.1, endTime - startTime);
    const lastEnd = this.segmentEnd(normalized) ?? 0;
    if (lastEnd <= renderDuration + 0.5) {
      return normalized.map(segment => ({
        start: startTime + Math.max(0, segment.start),
        end: startTime + Math.max(0, segment.end),
        text: segment.text
      }));
    }

    return normalized;
  }

  private segmentsInRange(segments: ReviewCaptionSegment[], startTime: number, endTime: number, shiftToZero: boolean): ReviewCaptionSegment[] {
    return this.normalizeSegments(segments)
      .filter(segment => segment.end > startTime && segment.start < endTime)
      .map(segment => ({
        start: shiftToZero ? Math.max(0, segment.start - startTime) : segment.start,
        end: shiftToZero ? Math.min(endTime, segment.end) - startTime : segment.end,
        text: segment.text
      }))
      .filter(segment => segment.end > segment.start && !!segment.text);
  }

  private descriptionFromPayload(value: string | null | undefined, sourceText: string, fallback: string): string {
    const clean = (value || '').replace(/\s+/g, ' ').trim();
    if (clean && !this.looksLikeTranscript(clean, sourceText)) {
      return clean.length <= 2500 ? clean : `${clean.slice(0, 2500).trimEnd()}...`;
    }

    // No usable description here: leave it blank so the backend metadata
    // writer generates a real one from the script at save time.
    return '';
  }

  private looksLikeTranscript(value: string, sourceText: string): boolean {
    const left = this.normalizeForCompare(value);
    const right = this.normalizeForCompare(sourceText);
    if (!left || !right) return false;
    if (left === right) return true;
    return left.length > 240 && left.startsWith(right.slice(0, 180));
  }

  private normalizeForCompare(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]+/g, '').trim();
  }

  private hookFromText(value: string): string {
    const clean = value.replace(/\s+/g, ' ').trim();
    return clean.length <= 180 ? clean : `${clean.slice(0, 180).trim()}...`;
  }

  private segmentEnd(segments: ReviewCaptionSegment[]): number | null {
    const end = segments.reduce((max, segment) => Math.max(max, segment.end), 0);
    return end > 0 ? end : null;
  }

  private titleFromFileName(fileName: string): string {
    const clean = fileName.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
    return clean || 'Runtime Processing Video';
  }

  private fileNameFromPath(filePath: string): string {
    return filePath.replace(/\\/g, '/').split('/').filter(Boolean).pop() || 'review-render.mp4';
  }

  private optionalString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private positiveNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private thumbnailCount(value: unknown): number {
    const parsed = Math.trunc(Number(value));
    return Math.max(1, Math.min(15, Number.isFinite(parsed) && parsed > 0 ? parsed : 1));
  }

  private finiteNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
